import Actor from '../physics/Actor';
import Player from '../physics/Player';
import common from '../common';

export default class ObjectTile extends Actor {
	constructor(mesh, filename, base = true, offset, rotation, scale) {
		super(mesh);

		this._filename = filename;
		this._base = base;
		this._model = null;
		this._canActivate = true;
		this._modelRotation = rotation;
		this._objectType = ObjectTile.NONE;

		this.tweens = new P3.utils.TWEEN.Group();

		this.initTile(offset, rotation, scale);
	}

	initTile(offset, rotation, scale) {
		if (!this._base) {
			this.mesh.dispose();
		}

		this._model = common.assets.model(this._filename);
		this._model.position.add(offset);
		this._model.scale.copy(scale);
		this._model.rotateZ(rotation * PIXI.DEG_TO_RAD);
		this.add(this._model);

		if (this._filename === 'lab_terminal') {
			this._objectType = ObjectTile.TERMINAL;
			this._canActivate = true;
		}
	}

	/**
	 * @param {Player} player
	 * @param {function} callback
	 */
	activate(player, callback) {
		const savedCharacter = player.currentCharacter;
		const modelRotation = Math.ceil(this.modelRotation / 90) * 90;
		const playerRotation = Math.ceil(((player.mesh.rotation.y * PIXI.RAD_TO_DEG) / 90) * 90);
		let playerState = player.mesh.spine.state;

		// first transform to ben
		this.transform(player, 'ben')
			.then(() => new Promise((resolve) => {
				playerState = player.mesh.spine.state;
				player.rotate(modelRotation * PIXI.DEG_TO_RAD, 400, false, true, () => {
					resolve();
				});
			}))
			.then(() => new Promise((resolve) => {
				// then type on the terminal
				P3.audio.playSound('sfx_terminal_typing_00');
				playerState.setAnimation(0, 'terminal', false);
				playerState.tracks[0].listener = {
					complete: () => {
						resolve();
					}
				};
			}))
			.then(() => new Promise((resolve) => {
				// then zoom out camera & activate tiles. Camera zoom only on level 13
				if (common.currentLevel === 12) {
					this.animateLevel13Camera(resolve);
				} else {
					for (const link of this.links) {
						link.receiver.activate();
					}

					resolve();
				}
			}))
			.then(() => new Promise((resolve) => {
				// then make camera go back, transform to previous character
				this.transform(player, savedCharacter)
					.then(() => resolve());
			}))
			.then(() => {
				player.rotate(playerRotation * PIXI.DEG_TO_RAD, 400, false, true, () => {
					callback();
				});
			});
	}

	/**
	 * @param {Player} player
	 * @param {string} who
	 */
	transform(player, who) {
		return new Promise((resolve) => {
			player.transform(who, false, () => {
				resolve();
			});
		});
	}

	animateLevel13Camera(resolve) {
		const targetRotation = new THREE.Quaternion(-0.2335, -0.3207, -0.0819, 0.9142);
		const targetVector = new THREE.Vector3(-8, 6, 20);
		const originalRotation = common.camera.quaternion.clone();
		const orignalCameraPosition = common.camera.position.clone();

		const tweenTime = 1000;
		const slerpTimeFactor = 2;
		const easing = P3.utils.TWEEN.Easing.Quadratic.Out;

		const backToPlayer = new P3.utils.TWEEN.Tween(common.camera.position, this.tweens)
			.to(orignalCameraPosition, tweenTime)
			.easing(easing);

		const cameraToPlayer = new P3.utils.TWEEN.Tween({t: 0}, this.tweens)
			.to({t: 1}, tweenTime * slerpTimeFactor)
			.easing(easing)
			.onUpdate((object) => {
				common.camera.quaternion.slerp(originalRotation, object.t);
			})
			.onComplete(() => {
				resolve();
			});

		new P3.utils.TWEEN.Tween(common.camera.position, this.tweens)
			.to(targetVector, tweenTime)
			.easing(easing)
			.onComplete(() => {
				for (const link of this.links) {
					link.receiver.activate();
				}
			})
			.start();

		new P3.utils.TWEEN.Tween({t: 0}, this.tweens)
			.to({t: 1}, tweenTime * slerpTimeFactor)
			.easing(easing)
			.onUpdate((object) => {
				common.camera.quaternion.slerp(targetRotation, object.t);
			})
			.onComplete(() => {
				P3.utils.delayg(this.tweens, 1, () => {
					backToPlayer.start();
					cameraToPlayer.start();
				});
			})
			.start();
	}

	destroy() {
		super.destroy();
	}

	update() {
		this.tweens.update();
	}

	get canActivate() {
		return this._canActivate;
	}

	set canActivate(v) {
		this._canActivate = v;
	}

	get objectType() {
		return this._objectType;
	}

	get modelRotation() {
		return this._modelRotation;
	}
}

ObjectTile.NONE 		= -1;
ObjectTile.TERMINAL 	= 0;
